import axios from 'axios'
import classNames from 'classnames'
import { toast } from 'react-toastify'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'

import { requestGetCourseEnrollments } from '../../../../actions/enrollmentsBillingActions'
import { deserialize } from '../../../../utility/serialization'
import { debounce } from '../../../../utility/debounceHelper'
import { PER_PAGE_DEFAULT } from '../../../../misc/Constants'
import { DEFAULT_SORT_OPTION } from '../../../../utility/enrollmentsBilling'
import Axios from '../../../../axios'

import EnrollmentsBillingTable from '../../../enrollments-billing/components/EnrollmentsBillingTable'
import EnrollmentDetailDrawer from '../../../enrollments-billing/components/EnrollmentDetailDrawer'
import EnrollmentsFilterBar, { DEFAULT_FILTERS } from '../../../enrollments-billing/components/EnrollmentsFilterBar'
import EnrollmentsEmptyState from '../../../enrollments-billing/components/EnrollmentsEmptyState'
import MembersSearchInput from '../../../members/components/MembersSearchInput'
import useEnrollmentRowActions from '../../../enrollments-billing/hooks/useEnrollmentRowActions'

import classes from '../../../enrollments-billing/EnrollmentsBillingPage.module.scss'
import tabClasses from './EnrollmentsBillingTab.module.scss'

// Per-program Enrollments & Billing tab. Reuses the community-wide table/drawer/filters,
// scoped to a single course via GET /api/v1/courses/:course_id/enrollments.
const EnrollmentsBillingTab = () => {
  const { t } = useTranslation()
  const { id: courseId } = useParams()
  const communityId = useSelector(state => state.user.current_user.communities[0].id)
  const refreshToken = useSelector(state => state.programDashboard.enrollmentsRefreshToken)
  const lastRefreshToken = useRef(refreshToken)
  const abortController = useRef(null)

  const [enrollments, setEnrollments] = useState([])
  const [pagination, setPagination] = useState(null)
  const [searchText, setSearchText] = useState('')
  const [sortOption, setSortOption] = useState(DEFAULT_SORT_OPTION)
  const [filters, setFilters] = useState(DEFAULT_FILTERS)
  const [isLoading, setIsLoading] = useState(false)
  const [isSortLoading, setIsSortLoading] = useState(false)
  const [selectedEnrollment, setSelectedEnrollment] = useState(null)
  const [packageOptions, setPackageOptions] = useState([])

  // Packages for the filter dropdown (program filter is omitted — this view is a single program)
  useEffect(() => {
    Axios.get('/api/v1/packages')
      .then(response => {
        const data = deserialize(response.data)
        const list = Array.isArray(data.packages || data) ? (data.packages || data) : []
        setPackageOptions(list.map(p => ({ label: p.name, value: p.id })))
      })
      .catch(() => { /* ignore - filter will just have no package options */ })
  }, [])

  const fetchEnrollments = useCallback(
    ({ page, search, sortField, sortValue, currentFilters, append }) => {
      abortController.current?.abort()
      abortController.current = new AbortController()

      return requestGetCourseEnrollments({
        courseId,
        page,
        perPage: PER_PAGE_DEFAULT,
        search,
        sortField,
        sortValue,
        filters: buildFilterParams(currentFilters),
        signal: abortController.current?.signal,
      })
        .then(response => {
          const data = deserialize(response.data)
          setEnrollments(prev => (append ? [...prev, ...data.enrollments] : data.enrollments))
          setPagination(data.meta.pagination)
        })
    },
    [courseId]
  )

  const updateEnrollments = useCallback(
    (params) => {
      setIsLoading(true)
      fetchEnrollments({
        page: 1,
        search: searchText,
        append: false,
        sortField: sortOption.sortField,
        sortValue: sortOption.sortValue,
        currentFilters: filters,
        ...params,
      })
        .catch((res) => {
          if (!axios.isCancel(res)) {
            toast.error(t('enrollmentsBilling.page.genericError'), { position: toast.POSITION.TOP_RIGHT })
          }
        })
        .finally(() => { setIsLoading(false); setIsSortLoading(false) })
    },
    [fetchEnrollments, searchText, sortOption.sortField, sortOption.sortValue, filters, t]
  )

  useEffect(() => {
    updateEnrollments({})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Students are enrolled from the dashboard above this tab, which cannot reach this list
  useEffect(() => {
    if (lastRefreshToken.current === refreshToken) { return }

    lastRefreshToken.current = refreshToken
    updateEnrollments({})
  }, [refreshToken, updateEnrollments])

  const fetchMoreEnrollments = () => updateEnrollments({ page: pagination?.page + 1, append: true })

  const debouncedSearch = useMemo(
    () => debounce((search) => updateEnrollments({ search })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  const handleSearch = (e) => {
    setSearchText(e.target.value)
    debouncedSearch(e.target.value)
  }

  const handleSorting = useCallback((sortField, sortValue) => {
    setIsSortLoading(true)
    setSortOption({ sortField, sortValue })
    updateEnrollments({ sortField, sortValue })
  }, [updateEnrollments])

  const handleApplyFilters = useCallback((newFilters) => {
    setFilters(newFilters)
    updateEnrollments({ currentFilters: newFilters })
  }, [updateEnrollments])

  const handleClearAll = () => {
    setSearchText('')
    setFilters(DEFAULT_FILTERS)
    updateEnrollments({ search: '', currentFilters: DEFAULT_FILTERS })
  }

  const { openApproveModal, openDeclineModal, modals: rowActionModals } =
    useEnrollmentRowActions({ onActionComplete: () => updateEnrollments({}) })

  return (
    <main className={classNames(classes.wrapper, tabClasses.fill)}>
      <header className={classes.toolbar}>
        <div className={classes.leftGroup}>
          <div className={classes.searchWrapper}>
            <MembersSearchInput placeholder={t('enrollmentsBilling.page.searchPlaceholder')} value={searchText} onChange={handleSearch} />
          </div>
          <EnrollmentsFilterBar
            appliedFilters={filters}
            onApplyFilters={handleApplyFilters}
            programOptions={[]}
            packageOptions={packageOptions}
            hideProgramFilter
          />
        </div>
      </header>

      {enrollments?.length === 0 && !isLoading ? (
        <EnrollmentsEmptyState
          hasFilters={hasActiveFilters(filters)}
          hasSearch={!!searchText}
          onClearAll={handleClearAll}
        />
      ) : (
        <EnrollmentsBillingTable
          enrollments={enrollments}
          fetchData={fetchMoreEnrollments}
          onRowClick={setSelectedEnrollment}
          onApprove={openApproveModal}
          onDecline={openDeclineModal}
          pagination={pagination}
          selectedEnrollmentId={selectedEnrollment?.id}
          isLoading={isLoading}
          isSortLoading={isSortLoading}
          sortOption={sortOption}
          applySort={handleSorting}
          hideProgramColumns
        />
      )}

      {!!selectedEnrollment && (
        <EnrollmentDetailDrawer
          enrollment={selectedEnrollment}
          communityId={communityId}
          onClose={() => setSelectedEnrollment(null)}
          onActionComplete={() => updateEnrollments({})}
        />
      )}

      {rowActionModals}
    </main>
  )
}

const buildFilterParams = (filters) => {
  const params = {}
  if (filters?.enrollmentStatus?.length) { params.enrollmentStatus = filters.enrollmentStatus }
  if (filters?.invoiceStatus?.length) { params.invoiceStatus = filters.invoiceStatus }
  if (filters?.packageId) { params.packageId = filters.packageId }
  if (filters?.dateJoinedStart) { params.dateJoinedStart = filters.dateJoinedStart }
  if (filters?.dateJoinedEnd) { params.dateJoinedEnd = filters.dateJoinedEnd }
  return params
}

const hasActiveFilters = (filters) => (
  (filters.enrollmentStatus?.length > 0) ||
  (filters.invoiceStatus?.length > 0) ||
  !!filters.packageId ||
  !!filters.dateJoinedStart ||
  !!filters.dateJoinedEnd
)

export default EnrollmentsBillingTab
